import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { PasswordInputWithValidation } from '@features/onboarding/components/shared/PasswordInputWithValidation';
import { AuthLayout } from '@features/auth/layouts/AuthLayout';
import { useRegister } from '@features/auth/lib/useRegister';
import { useGeneralStore } from 'src/store';
import { Form, FormField, FormItem, FormControl } from '@components/ui/form';
import { FloatingInput } from '@components/ui/input-floating';
import { useIsW2Mode } from '@features/w2/store/w2-mode';
import { W2_SIGNIN_COPY } from '@features/w2/copy';
import { DevFillButton, DEVFILL_TID } from 'src/devtools/DevFillButton';
import { DEVFILL_ENABLED } from 'src/devtools/gate';
import { buildRegistration, fillWith } from 'src/devtools/fillers/auth';

export const RegisterSchema = z.object({
  firstName: z.string()
    .min(1, 'First name is required.')
    .refine((val) => val.trim().length > 0, 'First name cannot be only spaces.')
    .refine((val) => !val.startsWith(' '), 'First name cannot start with a space.'),
  lastName: z.string()
    .min(1, 'Last name is required.')
    .refine((val) => val.trim().length > 0, 'Last name cannot be only spaces.')
    .refine((val) => !val.startsWith(' '), 'Last name cannot start with a space.'),
  email: z.string().email('Please enter a valid email address.'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters long.')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter.')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter.')
    .regex(/[0-9]/, 'Password must contain at least one number.')
    .regex(/[^A-Za-z0-9\s]/, 'Password must contain at least one special character.'),
});

type RegisterFormData = z.infer<typeof RegisterSchema>;

const sanitizeNameInput = (value: string) =>
  value
    .replace(/[^A-Za-z\u00e9\u00f1\u00fc\u00f3\u00ed\u00e1\u00fa\u00e8\u00e0\u00f9\u00eb\u00ef\u00f6\u00e4\u00fc\u00ff\u00e7\u00f1\s\-'\.]/g, '')
    .replace(/<[^>]*>/gi, '')
    .replace(/script|javascript/gi, '')
    .replace(/\s+/g, ' ');

const DEFAULT_REGISTRATION_TITLE = 'Welcome!';
const DEFAULT_REGISTRATION_TEXT =
  "New here? You\u2019re in the right spot. We\u2019re excited to help you explore insurance options that fit your life and budget. To get started, simply create an account\u2014this keeps your information secure and lets you save your progress along the way. Let\u2019s find the coverage that works for you!";

function isEmptyHtml(html: string): boolean {
  const stripped = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  return stripped.length === 0;
}

interface RegistrationFormProps {
  /**
   * Overrides the default post-registration navigate (B2C → `/`). The W2 employee
   * flow passes its enroll step so this exact branded screen is reused in the
   * tokenized flow without leaving it.
   */
  onAuthenticated?: () => void;
  /** When set, the "log in" affordance switches in place instead of routing to
   * `/login` — used by the single-page W2 token flow. */
  onSwitchToLogin?: () => void;
  /**
   * Roster invite pre-fill (contract §Frontend). When set, seeds first/last/email
   * from the resolved roster row and LOCKS the email read-only — the W2 enroll
   * step claims the roster row BY EMAIL, so an edited email would fork a duplicate
   * row; names stay editable. Absent (the B2C default) → an empty, fully-editable
   * form, byte-identical to today.
   */
  prefill?: { firstName: string; lastName: string; email: string };
}

export const RegistrationForm: React.FC<RegistrationFormProps> = ({
  onAuthenticated,
  onSwitchToLogin,
  prefill,
}) => {
  const { register, error, isLoading, clear } = useRegister(onAuthenticated);
  const siteConfig = useGeneralStore((s) => s.siteConfig);
  // W2 mode ships a CODE-SIDE copy override (spec screen 1), applied only when the
  // tokenized employee flow owns this reused B2C screen — employer siteConfig is
  // never touched.
  const isW2 = useIsW2Mode();

  const registrationDescription = useMemo(() => {
    if (isW2) return W2_SIGNIN_COPY.body;
    const raw = siteConfig?.registrationText;
    if (!raw || isEmptyHtml(raw)) return DEFAULT_REGISTRATION_TEXT;
    return <span dangerouslySetInnerHTML={{ __html: raw }} />;
  }, [isW2, siteConfig?.registrationText]);

  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance | null>(null);

  // Env-guarded captcha: the Turnstile gate is enforced only when a site key is
  // configured (production). Locally/dev there is no key, so the widget can never
  // mint a token — without this guard SUBMIT would stay permanently disabled.
  // When no key is present we skip the widget + the token gate so a filled form
  // can register (the backend has a matching dev recaptcha bypass). Production
  // sets VITE_RECAPTCHA_SITE_KEY, so the captcha is still enforced there.
  const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY || '';
  const recaptchaEnabled = recaptchaSiteKey.length > 0;

  const handleCaptchaSuccess = useCallback((token: string) => {
    setRecaptchaToken(token);
  }, []);

  const handleCaptchaExpire = useCallback(() => {
    setRecaptchaToken(null);
  }, []);

  // Email is locked only when the roster pre-fill supplied it (claim-by-email).
  const emailLocked = Boolean(prefill);

  const [ disabledSubmit, setDisabledSubmit ] = useState(false);
  const form = useForm<RegisterFormData>({
    resolver: zodResolver(RegisterSchema),
    defaultValues: {
      firstName: prefill?.firstName ?? '',
      lastName: prefill?.lastName ?? '',
      email: prefill?.email ?? '',
      password: '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    // mode: 'onChange', // Enable real-time validation
  });

  const {
    formState: { errors },
    clearErrors,
  } = form;

  const handleNameChange = (
    fieldName: 'firstName' | 'lastName',
    value: string
  ) => {
    const sanitizedValue = sanitizeNameInput(value);
    form.setValue(fieldName, sanitizedValue, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });
    clearErrors(fieldName);
  };

  const onSubmit = async (data: RegisterFormData) => {
    const trimmedData = {
      ...data,
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      recaptchaToken: recaptchaToken || '',
    };
    await register(trimmedData);
    turnstileRef.current?.reset();
    setRecaptchaToken(null);
  };

  useEffect(() => {
    // Блокируем submit только при ошибках валидации формы
    const hasFormErrors = Object.values(errors).length > 0;
    setDisabledSubmit(hasFormErrors);
  }, [JSON.stringify(errors)]);

  // Clear error when email or password changes
  const firstName = form.watch('firstName');
  const lastName = form.watch('lastName');
  const email = form.watch('email');
  const password = form.watch('password');

  useEffect(() => {
    clear();
  }, [firstName, lastName, email, password, clear]);

  return (
    <AuthLayout
      title={
        isW2
          ? W2_SIGNIN_COPY.headline
          : siteConfig?.registrationTitle || DEFAULT_REGISTRATION_TITLE
      }
      description={registrationDescription}
      variant="register"
      titleClassName="text-[40px] max-md:text-[28px]"
      descriptionClassName="text-xl mt-4.5 leading-7 max-md:text-[14px] max-md:leading-5"
      headerClassName="2xl:pt-19.5 2xl:pl-6.5 max-md:pb-16 max-sm:pb-7"
      contentClassName="2xl:pt-22.5 2xl:pl-24"
    >
      <div className="w-full max-w-[450px]">
        {DEVFILL_ENABLED && (
          <DevFillButton
            testid={DEVFILL_TID.registration}
            label="Fill sign-up"
            onFill={() =>
              fillWith(buildRegistration, (v) => form.reset(v))
            }
          />
        )}
        <section className="max-w-110">
          <h1 className="font-extrabold text-4xl mb-6">Create an account here!</h1>
          {/* <h1 className="font-extrabold text-4xl mb-2">Let's get started!</h1> */}
          {/* <p className="text-[14px] max-w-[430px] max-sm:max-w-87 flex-wrap !font-secondary">
            If you're new here, just take a moment to create an account\u2014it's
            quick, easy, and lets you securely save your progress so you can
            return anytime. Already started an application? Simply log in to
            pick up right where you left off. The whole process is
            straightforward and usually takes less than 10 minutes!
          </p> */}
        </section>
        {/* <h3 className="text-xl font-extrabold mb-6 mt-8">
          New user? Create an account here:
        </h3> */}
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="max-w-[430px]"
            data-testid="registration-form"
          >
            <div className="flex gap-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field, fieldState }) => (
                  <FormItem className="w-full">
                    <FormControl>
                      <FloatingInput
                        id="firstName"
                        label="First name"
                        type="text"
                        data-testid="registration-first-name"
                        value={field.value}
                        onChange={(e) =>
                          handleNameChange('firstName', e.target.value)
                        }
                        onBlur={field.onBlur}
                        name={field.name}
                        inputClass="w-full mb-5 h-16 rounded-lg border border-[#B7B7B7] p-2 disabled:text-[#ffffff55] disabled:opacity-100"
                      />
                    </FormControl>
                    {fieldState.error && (
                      <p className="text-error text-sm -mt-4 mb-4">
                        {fieldState.error.message}
                      </p>
                    )}
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field, fieldState }) => (
                  <FormItem className="w-full">
                    <FormControl>
                      <FloatingInput
                        id="lastName"
                        label="Last name"
                        type="text"
                        data-testid="registration-last-name"
                        value={field.value}
                        onChange={(e) =>
                          handleNameChange('lastName', e.target.value)
                        }
                        onBlur={field.onBlur}
                        name={field.name}
                        inputClass="w-full mb-5 h-16 rounded-lg border border-[#B7B7B7] p-2 disabled:text-[#ffffff55] disabled:opacity-100"
                      />
                    </FormControl>
                    {fieldState.error && (
                      <p className="text-error text-sm -mt-4 mb-4">
                        {fieldState.error.message}
                      </p>
                    )}
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="email"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormControl>
                    <FloatingInput
                      id="email"
                      label="Email address"
                      type="email"
                      data-testid="registration-email"
                      value={field.value}
                      onChange={(e) => {
                        field.onChange(e);
                        clearErrors('email');
                      }}
                      onBlur={field.onBlur}
                      name={field.name}
                      readOnly={emailLocked}
                      inputClass="w-full max-w-107.5 mb-5 h-16 rounded-lg border border-[#B7B7B7] p-2 disabled:text-[#ffffff55] disabled:opacity-100"
                    />
                  </FormControl>
                  {fieldState.error && (
                    <p className="text-error text-sm -mt-4 mb-4">
                      {fieldState.error.message}
                    </p>
                  )}
                </FormItem>
              )}
            />

            <PasswordInputWithValidation
              form={form}
              onSubmit={form.handleSubmit(onSubmit)}
              isSubmitDisabled={
                disabledSubmit ||
                isLoading ||
                (recaptchaEnabled && !recaptchaToken)
              }
            >
              {error && (
                <p
                  data-testid="registration-error"
                  className="mt-4 mb-2 text-error text-sm text-center"
                >
                  {error}
                </p>
              )}
              {recaptchaEnabled && (
                <div className="mt-4 -mb-10 flex sm:justify-center lg:justify-start">
                  <Turnstile
                    ref={turnstileRef}
                    siteKey={recaptchaSiteKey}
                    onSuccess={handleCaptchaSuccess}
                    onExpire={handleCaptchaExpire}
                  />
                </div>
              )}
            </PasswordInputWithValidation>

            <p className="text-base xs:text-xl font-extrabold mt-10 text-center lg:text-left w-full max-w-[430px]">
              Returning user?{' '}
              {onSwitchToLogin ? (
                <button
                  type="button"
                  data-testid="registration-login-switch"
                  onClick={onSwitchToLogin}
                  className="text-primary hover:underline whitespace-nowrap"
                >
                  Click here to log in.
                </button>
              ) : (
                <Link
                  to="/login"
                  className="text-primary hover:underline whitespace-nowrap"
                >
                  Click here to log in.
                </Link>
              )}
            </p>
          </form>
        </Form>
      </div>
    </AuthLayout>
  );
};
